import { array } from 'fp-ts';
import { DropdownItem, IconName } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { FormSelectField, FormSelectFieldProps, LocalizedText } from '@mews/framework';
import { TooltippedIcon } from '@mews-commander/ui';
import { WithEnterpriseIds } from '../../../../../../types';
import { LockableField } from '../../../LockableField';
import { ACCOUNTING_CATEGORY_KEY_FIELD_NAME } from '../fieldNames';
import { WithFieldsConfig } from '../types';
import { getFieldLockedConfig } from '../helpers';
import { useCommonAccountingCategoriesKeys } from './useCommonAccountingCategories';

const renderItems = (keys: string[]) => keys.map((key) => <DropdownItem key={key} value={key} label={key} />);

type SelectFieldProps = Omit<FormSelectFieldProps, 'children' | 'name' | 'label' | 'required' | 'size' | 'helper'> & {
    disabledReasonTextKey?: Mews.TextKey;
    keys: string[];
    addEmptyOption?: boolean;
};

const SelectField = ({ disabledReasonTextKey, keys, addEmptyOption = false, ...other }: SelectFieldProps) => (
    <FormSelectField
        {...other}
        name={ACCOUNTING_CATEGORY_KEY_FIELD_NAME}
        label={<LocalizedText textKey={TextKey.AccountingCategoryKey} />}
        helper={disabledReasonTextKey ? <LocalizedText textKey={disabledReasonTextKey} /> : null}
        captionIconWithTooltip={
            <TooltippedIcon size="small" value={IconName.Help} tooltip={TextKey.AccountingCategoryKeyHelperText} />
        }
    >
        {addEmptyOption ? <DropdownItem value="" label="-" /> : null}
        {renderItems(keys)}
    </FormSelectField>
);

type AccountingCategoriesKeyFieldProps = WithEnterpriseIds &
    WithFieldsConfig & {
        isCreateForm: boolean;
    };

export const AccountingCategoriesKeyField = ({
    enterpriseIds,
    fieldsConfig,
    isCreateForm,
}: AccountingCategoriesKeyFieldProps) => {
    const isLocked = getFieldLockedConfig(ACCOUNTING_CATEGORY_KEY_FIELD_NAME, fieldsConfig);

    const { data: accountingCategories, isFetching } = useCommonAccountingCategoriesKeys({
        enterpriseIds,
    });

    const isEmpty = array.isEmpty(accountingCategories);
    const disabledReasonTextKey =
        enterpriseIds.length > 1 ? TextKey.NoCommonAccountingCategoriesPlural : TextKey.NoCommonAccountingCategories;

    return !isEmpty ? (
        <LockableField
            fieldName={ACCOUNTING_CATEGORY_KEY_FIELD_NAME}
            locked={isLocked}
            lockedReasonText={TextKey.RatesValuesAreDifferent}
            tooltipTextKey={TextKey.UnlockableRatesField}
        >
            {(isDisabled) => (
                <SelectField
                    disabled={isDisabled}
                    addEmptyOption={!isCreateForm}
                    loader={isFetching}
                    keys={accountingCategories}
                />
            )}
        </LockableField>
    ) : (
        <SelectField
            disabledReasonTextKey={!isFetching ? disabledReasonTextKey : undefined}
            loader={isFetching}
            keys={accountingCategories}
            disabled
        />
    );
};
